import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";
import { LogOut, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CreateMeetingModal from "./CreateMeetingModal";
import { userAPI, meetingAPI } from "../Services/apiService";
import { toast } from "sonner";
import { showToast } from "../utils/toast";
import { handleAPIError } from "../utils/errorHandler";
import ConfirmDialog from "./ui/confirm-dialog";

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Fetch users when modal opens and refresh every 5 seconds
  useEffect(() => {
    if (isCreateModalOpen) {
      const fetchUsers = async () => {
        // Only show loading on initial fetch, not on auto-refresh
        if (users.length === 0) {
          setIsLoadingUsers(true);
        }
        try {
          // Fetch all users using pagination (no limit)
          let allUsers = [];
          let page = 1;
          let hasMore = true;

          while (hasMore) {
            const response = await userAPI.getUsers({
              include_vendors: true,
              page: page,
              page_size: 200, // Max allowed by API
            });

            // Handle both paginated response and simple array
            if (response.data && Array.isArray(response.data)) {
              // Simple array response (backward compatibility)
              allUsers = response.data;
              hasMore = false;
            } else if (response.data && response.data.items) {
              // Paginated response
              allUsers = [...allUsers, ...response.data.items];
              hasMore = response.data.has_next || false;
              page++;
            } else {
              hasMore = false;
            }
          }

          setUsers(allUsers);
        } catch (error) {
          if (users.length === 0) {
            handleAPIError(error, "Failed to load users", true);
          }
        } finally {
          setIsLoadingUsers(false);
        }
      };

      // Initial fetch
      fetchUsers();

      // Auto-refresh every 5 seconds
      // const interval = setInterval(fetchUsers, 60000);

      // Listen for user status changes and refresh immediately
      const handleUserStatusChanged = () => {
        fetchUsers();
      };
      window.addEventListener("userStatusChanged", handleUserStatusChanged);

      // Cleanup interval and event listener on modal close
      return () => {
        window.removeEventListener(
          "userStatusChanged",
          handleUserStatusChanged
        );
      };
    }
  }, [isCreateModalOpen]);

  const handleCreateMeeting = async (meetingData, skipNotification = false) => {
    try {
      const response = await meetingAPI.createMeeting(
        meetingData,
        skipNotification
      );
      showToast.success("Meeting created successfully!");
      setIsCreateModalOpen(false);

      // Dispatch custom event to refresh data across the app
      window.dispatchEvent(new CustomEvent("meetingCreated"));

      // Return response for agenda document upload
      return response;
    } catch (error) {
      // Error is already handled by the modal, so just re-throw it
      throw error;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800";
      case "business_head":
        return "bg-blue-100 text-blue-800";
      case "employee":
        return "bg-green-100 text-green-800";
      case "vendor":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "admin":
        return "Administrator";
      case "business_head":
        return "Business Head";
      case "employee":
        return "Employee";
      case "vendor":
        return "Vendor";
      default:
        return "User";
    }
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 w-full shrink-0">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex flex-col sm:flex-row items-center space-x-3">
              <div className="flex gap-2">
                <div className="w-full h-auto bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-sm flex items-center justify-center py-1">
                  {/* <span className="text-white font-bold text-sm">MT</span> */}
                  <img src="/Mosil.png" alt="MeetTracker" className="w-28" />
                </div>
              </div>
            </div>

            {/* Welcome Message */}
            <div className="flex-1 text-center">
              <h1
                className="text-xl hidden  lg:block  font-bold text-gray-900"
                data-testid="welcome-message"
              >
                Welcome back, {user?.name}
              </h1>
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              <div className="hidden sm:block">
                <Button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="btn-gold w-full sm:w-auto"
                  data-testid="create-meeting-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Meeting
                </Button>
              </div>
              <Badge
                variant="secondary"
                className={`${getRoleBadgeColor(
                  user?.role
                )} border-0 text-xs  font-medium`}
                data-testid="user-role-badge"
              >
                {getRoleLabel(user?.role)}
              </Badge>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-8 w-8 rounded-full p-0"
                    data-testid="user-menu"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-yellow-100 text-yellow-800 text-sm font-semibold">
                        {getInitials(user?.name || "U")}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-fit" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p
                        className="text-sm font-medium leading-none"
                        data-testid="user-name"
                      >
                        {user?.name}
                      </p>
                      <p
                        className="text-xs leading-none text-wrap text-muted-foreground"
                        data-testid="user-email"
                      >
                        {user?.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />

                  <DropdownMenuItem
                    onClick={() => setIsLogoutModalOpen(true)}
                    className="flex items-center space-x-2 text-red-600 focus:text-red-600 cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto max-w-[95vw] lg:max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-2 w-full">
        {children}
      </main>

      {/* Create Meeting Modal */}
      <CreateMeetingModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateMeeting}
        users={users}
        isLoadingUsers={isLoadingUsers}
      />

      <ConfirmDialog
        open={isLogoutModalOpen}
        onOpenChange={setIsLogoutModalOpen}
        onConfirm={handleLogout}
        title="Log Out"
        description="Are you sure you want to log out?"
        confirmText="Log Out"
        cancelText="Cancel"
        variant="destructive"
        icon="warning"
      />
    </div>
  );
};

export default Layout;
